import React, { useMemo } from 'react';
import { createContext } from '@shared/utils/createContext';
import globalWindow from '@shared/core/globals';
import { FEATURE_FLAG_VALUES } from '@shared/core/featureFlags/featureFlags.constants';
import type { BootFlags } from './bootFlags.types';
import { BOOT_FLAG_HIDE_JOY_LOGO } from './bootFlags.constants';

/**
 * Client-side source: the global Html.tsx inlines. On the server `globalWindow` is a static
 * stub shared by every request, so SSR must take its value from the provider prop instead.
 */
const getBootFlagsFromWindow = (): BootFlags | undefined => globalWindow.__BOOT_FLAGS__;

/**
 * Boot flags for the current render. Reads the provider so SSR and the client resolve
 * identically — reading the window global directly would resolve to `undefined` on the
 * server and flip on hydration, which is exactly the mismatch this provider exists to avoid.
 *
 * `undefined` outside a provider (and when the server failed to deliver flags) is the same
 * signal, and every consumer already fails safe on it.
 */
const [Provider, useBootFlags] = createContext<BootFlags | undefined>({
  name: 'BootFlags',
  strict: false,
  initialValue: undefined,
});

export { useBootFlags };

type BootFlagsProviderProps = Readonly<{
  /** SSR only: `request.amplitudeBootFlags`, threaded through so the server render sees the
   * same values the client will read from `window.__BOOT_FLAGS__`. */
  initialBootFlags?: BootFlags;
}>;

export const BootFlagsProvider: React.FC<BootFlagsProviderProps> = ({ initialBootFlags, children }) => {
  const value = useMemo(() => initialBootFlags ?? getBootFlagsFromWindow(), [initialBootFlags]);
  return <Provider value={value}>{children}</Provider>;
};

/**
 * Mirrors how the live path resolves the flag: `useIsFeatureEnabled` discards any variant that
 * isn't in the flag's registered `supportedValues`. Reading the same registration here keeps the
 * boot decision and the live decision from diverging on a variant one side accepts (e.g.
 * 'treatment', which hideJoyLogo does not register today).
 */
const isOnValue = (value: string): boolean => {
  const { supportedValues } = FEATURE_FLAG_VALUES.hideJoyLogo;
  return (supportedValues as ReadonlyArray<string>).includes(value.toLowerCase());
};

/**
 * The server-resolved `hidejoylogo` boot flag.
 *
 * @returns undefined when the server failed to deliver boot flags — the server emits every key
 * with a resolved value, so an absent one is a failure, never "this visitor isn't flagged".
 */
export const useHideJoyLogoBootFlag = (): boolean | undefined => {
  const bootFlags = useBootFlags();
  const value = bootFlags?.[BOOT_FLAG_HIDE_JOY_LOGO];
  return value === undefined ? undefined : isOnValue(value);
};
